import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { NgxEchartsDirective } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';
import { ThemeService } from '../../services/theme.service';
import { chartColors, chartPalette } from './chart-theme';

export interface GpMarginTrendPoint {
  month: string;
  category: string;
  gpmPct: number | null;
}

@Component({
  selector: 'app-gp-margin-trend-chart',
  standalone: true,
  imports: [NgxEchartsDirective],
  host: { class: 'block w-full h-full' },
  templateUrl: './gp-margin-trend-chart.component.html',
})
export class GpMarginTrendChartComponent implements AfterViewInit, OnInit, OnChanges, OnDestroy {
  @Input() data: GpMarginTrendPoint[] | null = null;

  options: EChartsOption | null = null;
  readonly viewportSize = 12;
  viewportStart = 0;

  @ViewChild('chartContainer', { static: true }) private chartContainer?: ElementRef<HTMLElement>;

  private hasBuiltOptions = false;
  private themeSub?: Subscription;
  private chartInstance?: { resize: () => void };
  private resizeObserver?: ResizeObserver;
  private compactLayout = false;
  private dataReference: GpMarginTrendPoint[] | null = null;
  private lastWheelTime = 0;
  private readonly themeService = inject(ThemeService);

  ngOnInit(): void {
    if (!this.hasBuiltOptions) {
      this.buildOptions();
    }
    this.themeSub = this.themeService.isDark$.pipe(skip(1)).subscribe(() => this.buildOptions());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data !== this.dataReference) { this.dataReference = this.data; this.viewportStart = 0; }
    this.buildOptions();
  }

  ngAfterViewInit(): void {
    if (!this.chartContainer?.nativeElement) return;
    this.chartContainer.nativeElement.addEventListener('wheel', this.onNativeWheel, { passive: false, capture: true });
    this.resizeObserver = new ResizeObserver(() => {
      const compact = this.isCompactLayout();
      if (compact !== this.compactLayout) {
        this.compactLayout = compact;
        this.buildOptions();
      }
      this.chartInstance?.resize();
    });
    this.resizeObserver.observe(this.chartContainer.nativeElement);
  }

  ngOnDestroy(): void {
    this.themeSub?.unsubscribe();
    this.resizeObserver?.disconnect();
    this.chartContainer?.nativeElement.removeEventListener('wheel', this.onNativeWheel, true);
  }

  onChartInit(instance: { resize: () => void }): void {
    this.chartInstance = instance;
    this.chartInstance.resize();
  }

  get hasScrollableViewport(): boolean { return this.allMonths().length > this.viewportSize; }
  get canMoveOlder(): boolean { return this.viewportStart > 0; }
  get canMoveNewer(): boolean { return this.viewportStart < Math.max(0, this.allMonths().length - this.viewportSize); }
  get viewportLabel(): string { const months = this.visibleMonths(); return months.length ? `${months[0]} – ${months[months.length - 1]}` : ''; }
  moveViewport(direction: 'older' | 'newer'): void {
    const max = Math.max(0, this.allMonths().length - this.viewportSize);
    const next = Math.max(0, Math.min(max, this.viewportStart + (direction === 'newer' ? 1 : -1)));
    if (next === this.viewportStart) return;
    this.viewportStart = next;
    this.buildOptions();
  }
  private readonly onNativeWheel = (event: WheelEvent): void => {
    if (!this.hasScrollableViewport) return;
    event.preventDefault();
    event.stopPropagation();
    const now = Date.now();
    if (now - this.lastWheelTime < 120) return;
    this.lastWheelTime = now;
    this.moveViewport(event.deltaY > 0 ? 'newer' : 'older');
  };

  private buildOptions(): void {
    this.hasBuiltOptions = true;
    const visibleMonths = new Set(this.visibleMonths());
    const rows = this.hasScrollableViewport ? (this.data ?? []).filter(row => visibleMonths.has(row.month)) : (this.data ?? []);
    const c = chartColors();
    const allCategories = Array.from(new Set(rows.map(row => row.category).filter(Boolean)));
    const categories = allCategories.filter(category => rows.some(row =>
      row.category === category
      && row.gpmPct !== null
      && Number.isFinite(row.gpmPct)
      && row.gpmPct !== 0
    ));
    const months = Array.from(new Set(rows.map(row => row.month).filter(Boolean)));
    const valuesByCategory = new Map<string, Map<string, number | null>>();
    rows.forEach(row => {
      if (!valuesByCategory.has(row.category)) valuesByCategory.set(row.category, new Map());
      valuesByCategory.get(row.category)!.set(row.month, row.gpmPct);
    });
    const palette = chartPalette();
    const categoryColors = Object.fromEntries(
      allCategories.map((category, index) => [category, palette[index % palette.length] || c.primary])
    );
    const numericValues = rows.map(row => row.gpmPct)
      .filter((value): value is number => value !== null && Number.isFinite(value));
    const compact = this.isCompactLayout();
    this.compactLayout = compact;
    const yAxis = this.calculateYAxis(numericValues, compact ? 6 : 8);

    this.options = {
      grid: { left: compact ? 28 : 34, right: 12, top: 34, bottom: compact ? 72 : 76, containLabel: true },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: c.chartSurface,
        borderColor: c.cardBorder,
        borderWidth: 1,
        padding: [10, 12],
        textStyle: { color: c.textPrimary, fontSize: 12 },
        extraCssText: 'box-shadow: 0 12px 28px rgba(0,0,0,0.16); border-radius: 8px;',
        valueFormatter: value => `${Number(value).toFixed(1)}%`,
      },
      legend: {
        show: true,
        bottom: 0,
        itemGap: 16,
        icon: 'rect',
        data: categories,
        textStyle: { color: c.textSecondary, fontSize: 11, fontWeight: 500 },
      },
      xAxis: {
        type: 'category',
        boundaryGap: true,
        data: months,
        axisLine: { lineStyle: { color: c.cardBorder } },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: {
          color: c.textSecondary,
          fontSize: compact ? 10 : 11,
          margin: 12,
          interval: 0,
          rotate: compact ? 25 : 35,
        },
      },
      yAxis: {
        type: 'value',
        min: yAxis.min,
        max: yAxis.max,
        interval: yAxis.interval,
        splitNumber: compact ? 6 : 8,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: c.textSecondary,
          fontSize: compact ? 10 : 11,
          hideOverlap: true,
          showMinLabel: true,
          showMaxLabel: true,
          formatter: (value: number) => `${value}%`,
        },
        splitLine: { lineStyle: { color: c.cardBorder, type: 'dashed', opacity: 0.75 } },
      },
      series: categories.map(category => {
        const color = categoryColors[category];
        const data = months.flatMap((month, monthIndex) => {
          const availableCategories = categories.filter(name => {
            const value = valuesByCategory.get(name)?.get(month);
            return value !== null && value !== undefined && Number.isFinite(value);
          });
          const value = valuesByCategory.get(category)?.get(month);
          const slotIndex = availableCategories.indexOf(category);

          return value === null || value === undefined || !Number.isFinite(value) || slotIndex < 0
            ? []
            : [[monthIndex, value, slotIndex, availableCategories.length]];
        });

        return {
          name: category,
          type: 'custom',
          data,
          encode: { x: 0, y: 1, tooltip: 1 },
          renderItem: (_params: any, api: any) => {
            const monthIndex = api.value(0);
            const value = Number(api.value(1));
            const slotIndex = Number(api.value(2));
            const accountCount = Math.max(1, Number(api.value(3)));
            const categoryWidth = Math.abs(api.size([1, 0])[0]);
            const groupWidth = Math.min(categoryWidth * 0.9, accountCount * 34);
            const slotWidth = groupWidth / accountCount;
            const barWidth = Math.min(24, slotWidth * 0.76);
            const center = api.coord([monthIndex, 0])[0];
            const zeroY = api.coord([monthIndex, 0])[1];
            const valueY = api.coord([monthIndex, value])[1];
            const x = center - (groupWidth / 2) + (slotIndex * slotWidth) + ((slotWidth - barWidth) / 2);
            const y = Math.min(zeroY, valueY);
            const height = Math.max(1, Math.abs(zeroY - valueY));
            const labelOffset = (slotIndex % 2) * 12;
            const labelY = value >= 0 ? y - 6 - labelOffset : y + height + 6 + labelOffset;

            return {
              type: 'group',
              children: [
                {
                  type: 'rect',
                  shape: { x, y, width: barWidth, height, r: value >= 0 ? [4, 4, 0, 0] : [0, 0, 4, 4] },
                  style: { fill: color },
                },
                {
                  type: 'text',
                  style: {
                    x: x + (barWidth / 2),
                    y: labelY,
                    text: `${Math.round(value)}%`,
                    fill: c.textPrimary,
                    font: `${compact ? 9 : 10}px sans-serif`,
                    align: 'center',
                    verticalAlign: value >= 0 ? 'bottom' : 'top',
                  },
                },
              ],
            };
          },
          color,
          itemStyle: { color },
        };
      }),
    };
  }

  private isCompactLayout(): boolean {
    const width = this.chartContainer?.nativeElement.clientWidth ?? window.innerWidth;
    return window.innerWidth <= 1366 || width < 620;
  }

  private calculateYAxis(values: number[], maximumIntervals: number): { min: number; max: number; interval: number } {
    if (values.length === 0) return { min: 0, max: 10, interval: 10 };

    const dataMin = Math.min(0, ...values);
    const dataMax = Math.max(0, ...values);
    const range = dataMax - dataMin;
    const interval = this.niceInterval(range / Math.max(1, maximumIntervals));
    const min = Math.floor(dataMin / interval) * interval;
    let max = Math.ceil(dataMax / interval) * interval;

    if (max === min) max = min + interval;
    return { min, max, interval };
  }

  private niceInterval(value: number): number {
    if (!Number.isFinite(value) || value <= 0) return 10;

    const magnitude = 10 ** Math.floor(Math.log10(value));
    const normalized = value / magnitude;
    const multiplier = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
    return multiplier * magnitude;
  }

  private allMonths(): string[] { return Array.from(new Set((this.data ?? []).map(row => row.month).filter(Boolean))); }
  private visibleMonths(): string[] { return this.allMonths().slice(this.viewportStart, this.viewportStart + this.viewportSize); }
}
